import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { System } from '@/lib/api/wines';
import { Badge, Card, EmptyState, Icon, Skeleton } from '@/design-system';

/**
 * /admin/providers — read-only health view for the integrations behind the
 * scanning + AI stack. Surfaces what /health already exposes:
 *   - barcode providers (configured / missing api key / public)
 *   - Vivino enrichment toggle
 *   - OpenAI vision + text models
 *   - recommendations engine status
 *   - local print queue mode
 *
 * The intent is operational: when a scan misfires or recommendations
 * stop responding, an owner can land here and see immediately whether
 * a key is missing or a provider is intentionally disabled.
 */
export function AdminProvidersPage() {
  const healthQ = useQuery({
    queryKey: ['admin.health'],
    queryFn: System.health,
    refetchInterval: 30_000,
    staleTime: 0,
  });

  return (
    <div className="grid gap-6 max-w-4xl">
      <div className="grid gap-3">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-sm text-mist hover:text-candle"
        >
          <Icon name="chevron-left" size={14} /> Tilbake til admin
        </Link>
        <header className="grid gap-2">
          <h1 className="ui-section-title text-3xl">Integrasjoner</h1>
          <p className="text-sm text-mist">
            Status for strekkode-tjenestene, AI-modellene og print-køen. Oppdateres
            hvert 30. sekund.
          </p>
        </header>
      </div>

      {healthQ.isLoading ? (
        <Card className="grid gap-3 p-5">
          <Skeleton height={20} width="40%" />
          <Skeleton height={48} /><Skeleton height={48} />
        </Card>
      ) : healthQ.isError || !healthQ.data ? (
        <Card className="p-5">
          <EmptyState
            icon="warning"
            title="Kunne ikke laste status"
            description="Prøv igjen om litt — backend svarte ikke på /health."
          />
        </Card>
      ) : (
        <>
          <ProviderSection
            title="Strekkode-tjenester"
            description="Lookup-tjenester konsultert i tillegg til primærkatalogen."
            icon="barcode"
            items={healthQ.data.providers.map((p) => ({
              key: p.name,
              label: p.name,
              enabled: p.enabled,
              detail: providerDetail(p),
            }))}
          />

          <ProviderSection
            title="Berikelse"
            description="Eksterne kilder for ratings og smaks-data."
            icon="sparkles"
            items={[
              {
                key: 'vivino',
                label: 'Vivino CLI',
                enabled: healthQ.data.enrichment.vivino.enabled,
                detail: healthQ.data.enrichment.vivino.enabled
                  ? 'Aktiv'
                  : 'Slått av i konfigurasjon',
              },
            ]}
          />

          {healthQ.data.ai && (
            <ProviderSection
              title="AI-modeller"
              description="Brukes for etikett-tolkning, anbefalinger og paring."
              icon="sparkles"
              items={[
                {
                  key: 'openai-vision',
                  label: 'OpenAI vision',
                  enabled: Boolean(healthQ.data.ai.openai?.enabled),
                  detail: healthQ.data.ai.openai?.visionModel ?? '—',
                },
                {
                  key: 'openai-text',
                  label: 'OpenAI text',
                  enabled: Boolean(healthQ.data.ai.openai?.enabled),
                  detail: healthQ.data.ai.openai?.textModel ?? '—',
                },
                {
                  key: 'recommendations',
                  label: 'Anbefalinger',
                  enabled: Boolean(healthQ.data.ai.recommendations?.enabled),
                  detail:
                    healthQ.data.ai.recommendations?.mode ??
                    healthQ.data.ai.recommendations?.model ??
                    '—',
                },
              ]}
            />
          )}

          <ProviderSection
            title="Print-kø"
            description="Lokal Zebra-skriver eller bakgrunnskø."
            icon="print"
            items={[
              {
                key: 'printing',
                label: `Modus: ${healthQ.data.printing.mode}`,
                enabled: healthQ.data.printing.ok ?? true,
                detail: healthQ.data.printing.defaultPrinterName ?? 'Ingen standard-skriver',
              },
            ]}
          />
        </>
      )}
    </div>
  );
}

function ProviderSection({
  title,
  description,
  icon,
  items,
}: {
  title: string;
  description: string;
  icon: 'barcode' | 'sparkles' | 'print';
  items: { key: string; label: string; enabled: boolean; detail?: string }[];
}) {
  return (
    <Card className="grid gap-3 p-5">
      <div className="flex items-center gap-3">
        <Icon name={icon} className="text-candle" />
        <h2 className="ui-section-title text-xl">{title}</h2>
      </div>
      <p className="text-xs text-mist">{description}</p>
      <ul className="grid gap-2">
        {items.map((item) => (
          <li
            key={item.key}
            className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-md border border-line-soft p-3"
          >
            <div className="grid">
              <span className="font-serif text-sm text-parchment">{item.label}</span>
              {item.detail && <span className="text-[11px] text-mist line-clamp-1">{item.detail}</span>}
            </div>
            <Badge tone={item.enabled ? 'gold' : 'mist'}>
              {item.enabled ? 'aktiv' : 'av'}
            </Badge>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function providerDetail(p: { reason?: string; auth?: string; baseUrl?: string }): string {
  const parts: string[] = [];
  if (p.reason === 'missing_api_key') parts.push('mangler API-nøkkel');
  else if (p.reason === 'public_provider') parts.push('åpen tjeneste');
  else if (p.reason === 'configured') parts.push('konfigurert');
  if (p.auth && p.auth !== 'none') parts.push(`auth: ${p.auth}`);
  if (p.baseUrl) parts.push(p.baseUrl);
  return parts.join(' · ');
}
